/**
 * Filter (rules) hooks — Vault-backed, account-scoped. Distinct from the legacy
 * useFilters (internal-mail), which the cleanup slice removes.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { MailboxFilterDto, MailboxFilterCondition, MailboxFilterAction } from '@/lib/api';

const mb = queryKeys.email.mailbox;

export function useEmailFilters(accountId: string | undefined) {
  return useQuery<MailboxFilterDto[]>({
    queryKey: mb.filters(accountId ?? ''),
    queryFn: async () => (await api.listEmailMailboxFilters(accountId as string)).filters,
    enabled: !!accountId,
  });
}

export function useCreateFilter(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      conditions: MailboxFilterCondition[];
      actions: MailboxFilterAction[];
      runAfterDays?: number | null;
    }) => api.createEmailMailboxFilter({ accountId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.filters(accountId) }),
  });
}

export function useToggleFilter(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ filterId, isEnabled }: { filterId: string; isEnabled: boolean }) =>
      api.updateEmailMailboxFilter(filterId, { accountId, isEnabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.filters(accountId) }),
  });
}

export function useDeleteFilter(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filterId: string) => api.deleteEmailMailboxFilter(filterId, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.filters(accountId) }),
  });
}
